/*
 OK FBI — Cinematic Arabic TTS for interrogation questions.
 Uses the browser's built-in SpeechSynthesis (works offline, zero-cost, OBS-friendly).
 Picks the best available Arabic voice, prefers "deep/male" heuristics.
 Never overlaps: any new speak() cancels the current utterance first.
*/

const STORAGE_KEY = "okfbi_tts";

let state = {
  enabled: true, rate: 0.9, pitch: 0.85, volume: 0.9, voiceURI: null,
  answersEnabled: true, answersVolume: 0.9,   // per-suspect voice master
};
try {
  const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  state = { ...state, ...s };
} catch {}

let cachedVoices = null;
let currentUtter = null;
const listeners = new Set(); // listeners for playing/paused state changes
let currentStatus = "idle"; // idle | speaking | paused
let currentTag = null;      // opaque id of the currently-speaking segment

function emit() { listeners.forEach(fn => { try { fn({ status: currentStatus, tag: currentTag }); } catch {} }); }
export function onStateChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function getStatus() { return { status: currentStatus, tag: currentTag }; }

function persist() { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {} }

export function getPrefs() { return { ...state }; }
export function setEnabled(v) { state.enabled = !!v; persist(); if (!v) stop(); }
export function setRate(v)    { state.rate = Math.max(0.5, Math.min(1.4, Number(v) || 0.9)); persist(); }
export function setPitch(v)   { state.pitch = Math.max(0.5, Math.min(1.5, Number(v) || 0.85)); persist(); }
export function setVolume(v)  { state.volume = Math.max(0, Math.min(1, Number(v) || 0.9)); persist(); }
export function setVoice(uri) { state.voiceURI = uri || null; persist(); }
export function isEnabled()   { return !!state.enabled; }
export function setAnswersEnabled(v) { state.answersEnabled = !!v; persist(); if (!v) stop(); }
export function isAnswersEnabled()   { return !!state.answersEnabled; }
export function setAnswersVolume(v)  { state.answersVolume = Math.max(0, Math.min(1, Number(v) || 0.9)); persist(); }

// ---- Per-suspect voice profile (deterministic; character continuity in audio) ----
function hashCode(s) { let h = 0; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; } return Math.abs(h); }

// Names that are typically female in Arabic naming
const FEMALE_NAMES = ["ريم","ليان","نورا","دانا","سارة","هدى","لينا","مي","هالة","رانيا","ليلى","مريم","فاطمة","عائشة","خديجة","نور","لارا","تالا","جوري"];
function inferGender(suspect) {
  if (!suspect?.name) return "male";
  const first = suspect.name.split(/\s+/)[0];
  return FEMALE_NAMES.some(n => first.includes(n)) ? "female" : "male";
}

/** Return {voice, rate, pitch, volume} tuned for the given suspect.
 *  Deterministic — same suspect always sounds the same (character continuity). */
export function voiceProfileFor(suspect) {
  const voices = listVoices();
  const gender = inferGender(suspect);
  const age = Number(suspect?.age) || 35;
  const seed = hashCode(suspect?.id || suspect?.name || "unknown");
  // pool by gender / language
  const arabic = voices.filter(v => /^ar[-_]/i.test(v.lang));
  const femalePattern = /(hoda|zeina|salma|amal|female|zahra|fatima)/i;
  const malePattern   = /(male|maged|hamed|khaled|nizar|amir|omar|nasser|fahd|ali)/i;
  let pool = arabic;
  if (arabic.length >= 2) {
    const filtered = arabic.filter(v => gender === "female" ? femalePattern.test(v.name) : malePattern.test(v.name));
    if (filtered.length) pool = filtered;
  }
  const v = pool[seed % Math.max(1, pool.length)] || voices[seed % Math.max(1, voices.length)] || null;
  // pitch/rate variation deterministic per suspect
  const basePitch = gender === "female" ? 1.08 : 0.82;
  const pitchJitter = ((seed % 17) - 8) * 0.01; // ±0.08
  const rateBase = 0.9;
  const rateJitter = ((seed % 11) - 5) * 0.02;  // ±0.10
  // Age influences pitch/rate subtly
  const ageMod = age > 55 ? -0.05 : age < 30 ? 0.05 : 0;
  return {
    voice: v,
    rate: Math.max(0.6, Math.min(1.2, rateBase + rateJitter)),
    pitch: Math.max(0.5, Math.min(1.3, basePitch + pitchJitter + ageMod)),
    volume: state.answersVolume,
    gender, seed, lang: (v?.lang || "ar-SA"),
  };
}

/** Speak a suspect's answer with the character-specific profile. */
export function speakAs(suspect, text, tag = null) {
  if (!state.answersEnabled) return false;
  if (!("speechSynthesis" in window) || !text) return false;
  window.speechSynthesis.cancel();
  const p = voiceProfileFor(suspect);
  const u = new SpeechSynthesisUtterance(text);
  if (p.voice) { u.voice = p.voice; u.lang = p.voice.lang || "ar-SA"; }
  else u.lang = "ar-SA";
  u.rate = p.rate; u.pitch = p.pitch; u.volume = p.volume;
  u.onstart = () => { currentStatus = "speaking"; currentTag = tag; emit(); };
  u.onend = () => { if (currentUtter === u) { currentStatus = "idle"; currentTag = null; emit(); currentUtter = null; } };
  u.onerror = () => { if (currentUtter === u) { currentStatus = "idle"; currentTag = null; emit(); currentUtter = null; } };
  currentUtter = u;
  currentTag = tag; currentStatus = "speaking"; emit();
  window.speechSynthesis.speak(u);
  return true;
}

/** Human-readable voice fingerprint (metadata only, host-only). */
export function voiceFingerprintFor(suspect) {
  const p = voiceProfileFor(suspect);
  const gender = p.gender === "female" ? "أنثى" : "ذكر";
  const tone = p.pitch >= 1.05 ? "حاد" : p.pitch >= 0.95 ? "متوسط" : p.pitch >= 0.80 ? "دافئ" : "عميق";
  const speed = p.rate >= 1.05 ? "سريع" : p.rate >= 0.95 ? "معتدل" : p.rate >= 0.82 ? "متأن" : "بطيء جداً";
  const cadence = ((p.seed % 5)) === 0 ? "متردد" : ((p.seed % 3) === 0) ? "قاطع" : "متدفّق";
  const timbre = p.gender === "female"
    ? (p.pitch > 1.05 ? "رنان أنثوي" : "أنثوي هادئ")
    : (p.pitch < 0.78 ? "جهوري عميق" : p.pitch > 0.88 ? "شبابي" : "خشن معتدل");
  return {
    voice_name: p.voice?.name || "auto",
    voice_lang: p.voice?.lang || p.lang || "ar-SA",
    gender,
    tone,          // نغمة
    speed,         // سرعة
    cadence,       // إيقاع
    timbre,        // خامة الصوت
    pitch: p.pitch,
    rate: p.rate,
    lang: p.lang,
    seed: p.seed,
  };
}

/** List available voices — prefer Arabic voices */
export function listVoices() {
  if (!("speechSynthesis" in window)) return [];
  if (cachedVoices) return cachedVoices;
  const all = window.speechSynthesis.getVoices() || [];
  const arabic = all.filter(v => /^ar[-_]/i.test(v.lang) || /arab/i.test(v.name));
  cachedVoices = arabic.length ? arabic : all;
  return cachedVoices;
}
// Trigger async voice list load (Chrome asynchronously populates voices)
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  window.speechSynthesis.onvoiceschanged = () => { cachedVoices = null; listVoices(); };
}

function pickVoice() {
  const voices = listVoices();
  if (!voices.length) return null;
  if (state.voiceURI) {
    const v = voices.find(x => x.voiceURI === state.voiceURI);
    if (v) return v;
  }
  // Preference heuristic: Arabic + male-ish name
  const preferMale = /(male|maged|hoda|rashid|hamed|khaled|nizar|nasser|amir|majed|omar|fahd)/i;
  const arabic = voices.filter(v => /^ar[-_]/i.test(v.lang));
  if (arabic.length) {
    const male = arabic.find(v => preferMale.test(v.name));
    return male || arabic[0];
  }
  return voices[0];
}

export function stop() {
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  currentUtter = null;
  currentStatus = "idle"; currentTag = null;
  emit();
}

export function pause() {
  if (currentStatus !== "speaking") return;
  window.speechSynthesis.pause();
  currentStatus = "paused"; emit();
}

export function resume() {
  if (currentStatus !== "paused") return;
  window.speechSynthesis.resume();
  currentStatus = "speaking"; emit();
}

/** Speak a piece of text. `tag` is an opaque id used by the UI to know which question is active. */
export function speak(text, tag = null) {
  if (!state.enabled) return false;
  if (!("speechSynthesis" in window)) return false;
  if (!text) return false;
  // Always cancel to prevent overlap
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  const voice = pickVoice();
  if (voice) { u.voice = voice; u.lang = voice.lang || "ar-SA"; }
  else { u.lang = "ar-SA"; }
  u.rate = state.rate;
  u.pitch = state.pitch;
  u.volume = state.volume;
  u.onstart = () => { currentStatus = "speaking"; currentTag = tag; emit(); };
  u.onend = () => { if (currentUtter === u) { currentStatus = "idle"; currentTag = null; emit(); currentUtter = null; } };
  u.onerror = () => { if (currentUtter === u) { currentStatus = "idle"; currentTag = null; emit(); currentUtter = null; } };
  currentUtter = u;
  currentTag = tag; currentStatus = "speaking"; emit();
  window.speechSynthesis.speak(u);
  return true;
}
